import { Label } from '../ui/label';
import { BenefitToAdd } from './CircuitEditorialForm';
import { useCallback, useMemo } from 'react';
import { CircuitDayFormData } from '@/types/circuits/CircuitDaysFormData';
import HorizontalLine from '../ui/HorizontalLine';
import { CircuitBenefitType } from '@/types/circuits/CircuitBenefitType';
import clsx from 'clsx';
import {
  BenefitPriceWithFormula,
  CircuitDayPrices,
  PriceWithFormula,
} from '@/utils/circuits/prices/computeBenefitPrices';
import { formatPrice } from '@/utils/circuits/prices/formatPrice';
import { useCircuitStepPricing } from '@/hooks/useCircuitPrices';
import { EstimateFormData } from '@/types/estimates/EstimateFormData';
import { EstimateResponse } from '@/types/estimates/EstimateResponse';
import { useConvertPrice } from '@/hooks/useCurrencyExchange';
import { formatCurrency } from '@/utils/prices/formatCurrency';
import { useTranslation } from '@/i18n';
import { SupportedLocale } from '@/types/supported-locale.type';

interface CircuitDayPricingProps {
  index: number;
  day: CircuitDayFormData;
  totalPrice: number;
  circuitDayPrices: CircuitDayPrices;
  date: Date;
  currency?: string;
  PricingComponent?: React.FC<CircuitStepsPricingProps>;
  selectedOptions?: EstimateFormData['selectedEstimateOptions'];
  setTotals?: React.Dispatch<React.SetStateAction<number[][]>>;
  adults?: EstimateResponse['adults'];
  children?: EstimateResponse['children'];
  creationDate?: Date;
  language?: SupportedLocale;
}

const CircuitDayPricing = ({
  index,
  day,
  totalPrice,
  circuitDayPrices,
  date,
  currency = 'EUR',
  PricingComponent = CircuitStepsPricing,
  selectedOptions,
  setTotals,
  adults,
  children,
  creationDate,
  language,
}: CircuitDayPricingProps) => {
  const { t } = useTranslation();
  const steps = useMemo(() => day?.steps ?? [], [day?.steps]);

  return (
    <div className="flex flex-col gap-4 w-full" id={`day-${index}`}>
      <Label className="text-base">
        {t('circuits.editorial.dayPricing.pricing', {
          index: index + 1,
        })}
      </Label>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        <BenefitToAdd type="Transport" onClick={() => {}} isViewMode={true} />
        <BenefitToAdd type="Activités" onClick={() => {}} isViewMode={true} />
        <BenefitToAdd type="Repas" onClick={() => {}} isViewMode={true} />
        <BenefitToAdd
          type="Hébergements"
          onClick={() => {}}
          isViewMode={true}
        />
      </div>
      {steps.map((step, stepIndex) => (
        <PricingComponent
          key={`step-${index}-${stepIndex}`}
          index={index}
          stepIndex={stepIndex}
          step={step}
          circuitStepPricing={circuitDayPrices[stepIndex]}
          date={date}
          selectedOptions={selectedOptions}
          setTotals={setTotals}
          adults={adults}
          children={children}
          currency={currency}
          creationDate={creationDate}
          language={language}
        />
      ))}
      <div className="flex w-full justify-between p-4 rounded-lg bg-white">
        <h3 className="text-base font-semibold">
          {t('circuits.editorial.dayPricing.totalServices', {
            index: index + 1,
          })}
        </h3>
        <p className="text-base font-semibold">
          {formatPrice(totalPrice, false)} {formatCurrency(currency)}
        </p>
      </div>
    </div>
  );
};

export default CircuitDayPricing;

export interface CircuitStepsPricingProps {
  index: number;
  stepIndex: number;
  circuitStepPricing: CircuitDayPrices[number];
  date: Date;
  step: CircuitDayFormData['steps'][number];
  selectedOptions?: EstimateFormData['selectedEstimateOptions'];
  setTotals?: React.Dispatch<React.SetStateAction<number[][]>>;
  adults?: EstimateResponse['adults'];
  children?: EstimateResponse['children'];
  currency?: string;
  creationDate?: Date;
  language?: SupportedLocale;
}

const CircuitStepsPricing = ({
  index,
  stepIndex,
  circuitStepPricing,
  date,
  step,
  currency = 'EUR',
  language,
}: CircuitStepsPricingProps) => {
  const {
    standardTransport,
    standardActivity,
    standardMeal,
    standardAccommodation,
    optionalTransportBenefits,
    optionalActivityBenefits,
    optionalMealsBenefits,
    optionalAccommodationsBenefits,
    atLeastOneStandard,
    optionalLength,
  } = useCircuitStepPricing({
    circuitStepPricing,
    date,
    step,
    language,
  });

  return (
    <CircuitStepsPricingView
      index={index}
      stepIndex={stepIndex}
      atLeastOneStandard={atLeastOneStandard}
      optionalLength={optionalLength}
      standardTransport={standardTransport}
      standardActivity={standardActivity}
      standardMeal={standardMeal}
      standardAccommodation={standardAccommodation}
      optionalTransportBenefits={optionalTransportBenefits}
      optionalActivityBenefits={optionalActivityBenefits}
      optionalMealsBenefits={optionalMealsBenefits}
      optionalAccommodationsBenefits={optionalAccommodationsBenefits}
      currency={currency}
    />
  );
};

type standardBenefitNameWithPrice = {
  name: string;
  price?: PriceWithFormula;
  marginRate?: number;
  currency: string;
};

type optionalBenefitNameWithPrice = standardBenefitNameWithPrice & {
  id: string;
};

interface CircuitStepsPricingViewProps {
  index: number;
  stepIndex: number;
  atLeastOneStandard: boolean;
  optionalLength: number;
  standardTransport?: standardBenefitNameWithPrice;
  standardActivity?: standardBenefitNameWithPrice;
  standardMeal?: standardBenefitNameWithPrice;
  standardAccommodation?: standardBenefitNameWithPrice;
  optionalTransportBenefits: optionalBenefitNameWithPrice[];
  optionalActivityBenefits: optionalBenefitNameWithPrice[];
  optionalMealsBenefits: optionalBenefitNameWithPrice[];
  optionalAccommodationsBenefits: optionalBenefitNameWithPrice[];
  currency?: string;
}

export const CircuitStepsPricingView = ({
  index,
  stepIndex,
  atLeastOneStandard,
  optionalLength,
  standardTransport,
  standardActivity,
  standardMeal,
  standardAccommodation,
  optionalTransportBenefits,
  optionalActivityBenefits,
  optionalMealsBenefits,
  optionalAccommodationsBenefits,
  currency = 'EUR',
}: CircuitStepsPricingViewProps) => {
  const calculateBenefitPrice = useCallback(
    (
      { formula, marginRate, minimumPrice }: BenefitPriceWithFormula,
      currency: string,
    ) => {
      const priceWithMargin = minimumPrice * (1 + marginRate / 100);
      if (minimumPrice === 0)
        return {
          formula: '0',
          minimumPrice: 0,
        };

      const updatedFormula = formula.includes('+') ? `(${formula})` : formula;

      return {
        formula:
          marginRate > 0
            ? `${updatedFormula} ${formatCurrency(currency)} + ${Math.floor(marginRate)}%`
            : `${updatedFormula} ${formatCurrency(currency)}`,
        minimumPrice: priceWithMargin,
      };
    },
    [],
  );

  const buildBenefitDisplayPrice = useCallback(
    (
      rawPrice: PriceWithFormula | undefined,
      marginRate: number,
      benefitCurrency: string,
    ) => {
      if (!rawPrice) return undefined;
      return calculateBenefitPrice(
        {
          formula: rawPrice.formula ?? '',
          minimumPrice: rawPrice.minimumPrice,
          marginRate,
        },
        benefitCurrency,
      );
    },
    [calculateBenefitPrice],
  );

  return (
    <>
      {atLeastOneStandard && (
        <>
          <HorizontalLine />
          <Label className="text-gray-700">STANDARD</Label>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="flex flex-col gap-2">
              {standardTransport && (
                <BenefitElementDisplay
                  type="Transport"
                  title={standardTransport.name}
                  price={buildBenefitDisplayPrice(
                    standardTransport.price,
                    standardTransport.marginRate ?? 0,
                    standardTransport.currency,
                  )}
                  currency={standardTransport.currency}
                  targetCurrency={currency}
                />
              )}
            </div>
            <div className="flex flex-col gap-2">
              {standardActivity && (
                <BenefitElementDisplay
                  type="Activités"
                  title={standardActivity?.name}
                  price={buildBenefitDisplayPrice(
                    standardActivity.price,
                    standardActivity.marginRate ?? 0,
                    standardActivity.currency,
                  )}
                  currency={standardActivity.currency}
                  targetCurrency={currency}
                />
              )}
            </div>
            <div className="flex flex-col gap-2">
              {standardMeal && (
                <BenefitElementDisplay
                  type="Repas"
                  title={standardMeal.name}
                  price={buildBenefitDisplayPrice(
                    standardMeal.price,
                    standardMeal.marginRate ?? 0,
                    standardMeal.currency,
                  )}
                  currency={standardMeal.currency}
                  targetCurrency={currency}
                />
              )}
            </div>
            <div className="flex flex-col gap-2">
              {standardAccommodation && (
                <BenefitElementDisplay
                  type="Hébergements"
                  title={standardAccommodation.name}
                  price={buildBenefitDisplayPrice(
                    standardAccommodation.price,
                    standardAccommodation.marginRate ?? 0,
                    standardAccommodation.currency,
                  )}
                  currency={standardAccommodation.currency}
                  targetCurrency={currency}
                />
              )}
            </div>
          </div>
        </>
      )}
      {optionalLength > 0 && (
        <>
          {!atLeastOneStandard && <HorizontalLine />}
          <Label className="text-gray-700">OPTION(S)</Label>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="flex flex-col gap-2">
              {optionalTransportBenefits.map((item) => (
                <BenefitElementDisplay
                  key={`optional-transport-${index}-${stepIndex}-${item.id}`}
                  type="Transport"
                  title={item.name}
                  price={buildBenefitDisplayPrice(
                    item.price,
                    item.marginRate ?? 0,
                    item.currency,
                  )}
                  currency={item.currency}
                  targetCurrency={currency}
                />
              ))}
            </div>
            <div className="flex flex-col gap-2">
              {optionalActivityBenefits.map((item) => (
                <BenefitElementDisplay
                  key={`optional-activity-${index}-${stepIndex}-${item.id}`}
                  type="Activités"
                  title={item.name}
                  price={buildBenefitDisplayPrice(
                    item.price,
                    item.marginRate ?? 0,
                    item.currency,
                  )}
                  currency={item.currency}
                  targetCurrency={currency}
                />
              ))}
            </div>
            <div className="flex flex-col gap-2">
              {optionalMealsBenefits.map((item) => (
                <BenefitElementDisplay
                  key={`optional-meal-${index}-${stepIndex}-${item.id}`}
                  type="Repas"
                  title={item.name}
                  price={buildBenefitDisplayPrice(
                    item.price,
                    item.marginRate ?? 0,
                    item.currency,
                  )}
                  currency={item.currency}
                  targetCurrency={currency}
                />
              ))}
            </div>
            <div className="flex flex-col gap-2">
              {optionalAccommodationsBenefits.map((item) => (
                <BenefitElementDisplay
                  key={`optional-accommodation-${index}-${stepIndex}-${item.id}`}
                  type="Hébergements"
                  title={item.name}
                  price={buildBenefitDisplayPrice(
                    item.price,
                    item.marginRate ?? 0,
                    item.currency,
                  )}
                  currency={item.currency}
                  targetCurrency={currency}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
};

const BenefitElementDisplay = ({
  type,
  title,
  price,
  currency = 'EUR',
  targetCurrency,
  creationDate,
}: {
  type: CircuitBenefitType;
  title: string;
  price?: PriceWithFormula;
  currency?: string;
  targetCurrency?: string;
  creationDate?: Date;
}) => {
  const { t } = useTranslation();
  const { convertPrice } = useConvertPrice(creationDate);

  const convertedPrice = useMemo(() => {
    if (!targetCurrency) return price?.minimumPrice ?? 0;
    return convertPrice(price?.minimumPrice ?? 0, currency, targetCurrency);
  }, [convertPrice, currency, price, targetCurrency]);

  const formattedPrice = useMemo(() => {
    if (!price) return t('circuits.editorial.dayPricing.unavailablePrice');
    if (price.minimumPrice === Infinity) return `∞ ${formatCurrency(currency)}`;
    const base = `${price.formula} = ${formatPrice(price.minimumPrice, false)} ${formatCurrency(currency)}`;
    const converted =
      targetCurrency && targetCurrency !== currency
        ? ` = ${formatPrice(convertedPrice, false)} ${formatCurrency(targetCurrency)}`
        : '';
    return `${base}${converted}`;
  }, [convertedPrice, currency, price, t, targetCurrency]);

  return (
    <div
      className={clsx(
        'flex items-center justify-between px-3.5 py-2 border border-gray-300 rounded-lg w-full',
        {
          'bg-[#FFEBEB]': type === 'Transport',
          'bg-[#F3EBFF]': type === 'Activités',
          'bg-[#EBF6FF]': type === 'Repas',
          'bg-[#EBFFEF]': type === 'Hébergements',
        },
      )}
    >
      <div className="flex flex-col gap-2 w-full">
        <Label>{title}</Label>
        <p className="font-bold text-sm">{formattedPrice}</p>
      </div>
    </div>
  );
};
